import React, { useEffect, useState } from 'react';
import {
  apiErrorMessage,
  createReferral,
  getReferrals,
  getWorkspace,
  inviteWorkspaceMember,
  removeWorkspaceMember,
  updateWorkspace,
} from '../api/adpilotApi';
import ReferralFunnel from './ReferralFunnel';

const number = new Intl.NumberFormat('en-US');
const date = (value) => (value ? new Date(value).toLocaleDateString() : '—');

function ReferralsPage() {
  const [summary, setSummary] = useState(null);
  const [generated, setGenerated] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [workspace, setWorkspace] = useState(null);
  const [industry, setIndustry] = useState('');
  const [businessSize, setBusinessSize] = useState('');
  const [memberLabel, setMemberLabel] = useState('');
  const [memberRole, setMemberRole] = useState('employee');
  const [teamLink, setTeamLink] = useState(null);
  const [sharedResult, setSharedResult] = useState(false);

  const load = async () => {
    try {
      const [referrals, currentWorkspace] = await Promise.all([
        getReferrals(),
        getWorkspace().catch(() => null),
      ]);
      setSummary(referrals);
      setWorkspace(currentWorkspace);
      setIndustry(currentWorkspace?.profile?.industry || '');
      setBusinessSize(currentWorkspace?.profile?.business_size || '');
      setError(null);
    } catch (loadError) {
      setError(apiErrorMessage(loadError, 'Failed to load your referral network'));
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const referralLink = (token) => `${window.location.origin}/login?invite=${encodeURIComponent(token)}`;

  const copyLink = async (id, token) => {
    if (!navigator.clipboard || !token) return;
    try {
      await navigator.clipboard.writeText(referralLink(token));
      setCopiedId(id);
    } catch (copyError) {
      setError('Could not copy the link. Check your browser clipboard permission and try again.');
    }
  };

  const handleGenerate = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await createReferral();
      setGenerated({
        ...result,
        link: referralLink(result.share_token),
      });
      await load();
    } catch (generateError) {
      setError(apiErrorMessage(generateError, 'Failed to generate referral link'));
    } finally {
      setBusy(false);
    }
  };

  const handleShareResult = async () => {
    const outcome = summary?.outcome || {};
    const text = `AdPilot completed ${outcome.completed_analyses || 0} campaign analyses and safely applied ${outcome.applied_changes || 0} approved changes. Nothing changed without approval.`;
    try {
      if (navigator.share) await navigator.share({ title: 'My AdPilot result', text });
      else await navigator.clipboard.writeText(text);
      setSharedResult(true);
    } catch {
      setError('Could not share this result. Check your browser permission and try again.');
    }
  };

  const handleProfile = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      const result = await updateWorkspace({ industry, businessSize });
      setWorkspace((current) => ({ ...current, profile: result.profile }));
    } catch (profileError) {
      setError(apiErrorMessage(profileError, 'Failed to update workspace profile'));
    } finally {
      setBusy(false);
    }
  };

  const handleInviteMember = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      const result = await inviteWorkspaceMember({ label: memberLabel, role: memberRole });
      const link = referralLink(result.share_token);
      setTeamLink(link);
      navigator.clipboard?.writeText(link).catch(() => {});
      setMemberLabel('');
      await load();
    } catch (inviteError) {
      setError(apiErrorMessage(inviteError, 'Failed to invite collaborator'));
    } finally {
      setBusy(false);
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (!window.confirm('Remove this collaborator from the workspace?')) return;
    try {
      await removeWorkspaceMember(memberId);
      await load();
    } catch (removeError) {
      setError(apiErrorMessage(removeError, 'Failed to remove collaborator'));
    }
  };

  const community = summary?.community || {};
  const invitations = summary?.invitations || [];
  const benchmark = summary?.peer_benchmark || {};
  const outcome = summary?.outcome || {};

  return (
    <div className="page network-page">
      <header className="page-header">
        <div>
          <p className="eyebrow">AdPilot network</p>
          <h1 className="page-title">Share what works.</h1>
          <p className="page-subtitle">Invite another business to run its own campaign agent. Links are never tied to an email.</p>
        </div>
        {!summary && !error
          ? <span className="agent-online agent-online-loading" role="status"><span className="spinner" aria-hidden="true" />Loading network</span>
          : <span className="agent-online"><i />Sharing ready</span>}
      </header>

      {error && <div className="notice notice-error" role="alert">{error}</div>}
      {!summary && !error && <section className="page-loading" aria-busy="true" aria-label="Loading network"><div className="page-loading-metrics">{[1, 2, 3].map((item) => <span key={item} />)}</div><div className="card page-loading-card"><span className="loading-line loading-line-short" /><span className="loading-line" /><span className="loading-block" /></div></section>}

      {summary && (
        <>
          <section className="network-benchmarks" aria-label="Anonymous peer benchmarks">
            {benchmark.available ? (
              <>
                <div><strong>{number.format(benchmark.cohort_size)}</strong><span>Similar businesses · anonymous cohort</span></div>
                <div><strong>{number.format(benchmark.avg_completed_runs)}</strong><span>Average completed analyses</span></div>
                <div><strong>{number.format(benchmark.avg_applied_changes)}</strong><span>Average approved changes</span></div>
              </>
            ) : (
              <>
                <div><strong>{number.format(community.active_businesses || 0)}</strong><span>Businesses optimizing</span></div>
                <div><strong>{number.format(community.completed_runs || 0)}</strong><span>Agent runs completed</span></div>
                <div><strong>{number.format(community.applied_changes || 0)}</strong><span>Changes safely applied · peer data appears at 5 businesses</span></div>
              </>
            )}
          </section>

          {summary.eligible && (
            <section className="card network-result">
              <div><span className="eyebrow">Safe to share</span><h2>Your AdPilot result</h2><p>{number.format(outcome.completed_analyses || 0)} analyses completed · {number.format(outcome.applied_changes || 0)} approved changes safely applied. No budgets, campaign names, or account identifiers are included.</p></div>
              <button className="btn btn-secondary" type="button" onClick={handleShareResult}>{sharedResult ? 'Copied' : 'Share result'}</button>
            </section>
          )}

          <section className="card network-generator">
            <div className="section-heading" style={{ marginTop: 0 }}>
              <div><h2>Your invitation links</h2><span>Three active links at a time · each link is single-use</span></div>
              <span>{summary.slots_remaining} available</span>
            </div>
            {!summary.eligible ? (
              <div className="network-locked"><strong>Complete your first analysis to unlock sharing.</strong><span>Your prompt will appear after the campaign agent returns a successful result.</span></div>
            ) : (
              <button className="btn btn-primary" type="button" onClick={handleGenerate} disabled={busy || summary.slots_remaining === 0}>{busy ? 'Generating…' : 'Generate invitation link'}</button>
            )}
            {generated && (
              <div className="invite-secret" role="status">
                <div><strong>Invitation link ready</strong><code>{generated.link}</code><small>You can copy it again below until someone uses it.</small></div>
                <button className="btn btn-secondary" type="button" onClick={() => copyLink('new', generated.share_token)}>{copiedId === 'new' ? 'Copied' : 'Copy link'}</button>
              </div>
            )}
          </section>

          <section className="card admin-panel">
            <div className="section-heading" style={{ marginTop: 0 }}>
              <div><h2>Your referral funnel</h2><span>From invitation link to 30-day activity</span></div>
              <span>{summary.referral_funnel?.generated || 0} generated</span>
            </div>
            <ReferralFunnel metrics={summary.referral_funnel} label="Your referral funnel" />
            <div className="section-heading referral-detail-heading">
              <div><h2>Invitation details</h2><span>Activation means Meta connected and a successful agent run</span></div>
            </div>
            <div className="referral-list">
              {invitations.map((item) => {
                const stage = item.retained_at ? 'Active after 30 days' : item.first_applied_at ? 'First change applied' : item.activated_at ? 'Activated' : item.connected ? 'Meta connected' : item.status;
                return (
                  <div className="referral-row" key={item.id}>
                    <div><strong>AdPilot invitation</strong><small>Created {date(item.created_at)} · expires {date(item.expires_at)}</small></div>
                    <span>{item.open_count || 0} open{Number(item.open_count) === 1 ? '' : 's'}</span>
                    <em>{stage}</em>
                    {item.share_token && <button className="btn btn-secondary referral-copy" type="button" onClick={() => copyLink(item.id, item.share_token)}>{copiedId === item.id ? 'Copied' : 'Copy link'}</button>}
                  </div>
                );
              })}
              {invitations.length === 0 && <p className="card-copy">Generate your first link when your agent has completed an analysis.</p>}
            </div>
          </section>

          {workspace && (
            <section className="card admin-panel">
              <div className="section-heading" style={{ marginTop: 0 }}>
                <div><h2>Workspace collaborators</h2><span>Employees, agencies, and consultants share this brief and approval workflow</span></div>
                <span>{workspace.members.length} joined</span>
              </div>
              {workspace.owner ? (
                <>
                  <form className="invite-form workspace-profile" onSubmit={handleProfile}>
                    <label><span>Industry</span><select value={industry} onChange={(event) => setIndustry(event.target.value)} required><option value="">Choose</option><option value="automotive">Automotive</option><option value="home_services">Home services</option><option value="healthcare">Healthcare</option><option value="hospitality">Hospitality</option><option value="retail">Retail</option><option value="professional_services">Professional services</option><option value="other">Other</option></select></label>
                    <label><span>Business size</span><select value={businessSize} onChange={(event) => setBusinessSize(event.target.value)} required><option value="">Choose</option><option value="solo">Solo</option><option value="2-10">2–10</option><option value="11-50">11–50</option><option value="51+">51+</option></select></label>
                    <button className="btn btn-secondary" type="submit" disabled={busy}>Save profile</button>
                  </form>
                  <form className="invite-form" onSubmit={handleInviteMember}>
                    <label><span>Collaborator label</span><input value={memberLabel} onChange={(event) => setMemberLabel(event.target.value)} placeholder="Marketing agency or shop manager" maxLength="120" required /></label>
                    <label><span>Type</span><select value={memberRole} onChange={(event) => setMemberRole(event.target.value)}><option value="employee">Employee</option><option value="agency">Agency</option><option value="consultant">Consultant</option></select></label>
                    <button className="btn btn-primary" type="submit" disabled={busy}>{busy ? 'Creating…' : 'Invite collaborator'}</button>
                  </form>
                  {teamLink && <div className="invite-share-link" role="status"><code>{teamLink}</code><span>Copied · single-use</span></div>}
                </>
              ) : <div className="network-locked"><strong>You’re collaborating in this workspace.</strong><span>The owner manages Meta sources and workspace membership.</span></div>}
              <div className="referral-list">
                {workspace.members.map((member) => <div className="referral-row" key={member.id}><div><strong>{member.display_name || member.email}</strong><small>{member.email}</small></div><span>{member.role}</span><em>Joined</em>{workspace.owner && <button className="text-button text-button-danger" type="button" onClick={() => handleRemoveMember(member.id)}>Remove</button>}</div>)}
                {workspace.owner && workspace.invitations.filter((item) => item.share_token).map((item) => <div className="referral-row" key={item.id}><div><strong>{item.label}</strong><small>{item.role} · expires {date(item.expires_at)}</small></div><span>{item.status}</span><em>Pending</em><button className="btn btn-secondary referral-copy" type="button" onClick={() => copyLink(item.id, item.share_token)}>Copy link</button></div>)}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}

export default ReferralsPage;
